import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Refresh, Savings } from "@mui/icons-material";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../firebase/config";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import { aggregateCashOutAdminMetrics } from "../services/cashOut/cashOutAdminMetrics";
import { getRewardsRetirementWallet } from "../services/cashOut/rewardsRetirementWallet";

const formatInr = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const CashOutAdminFlowDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ledgerRows, setLedgerRows] = useState([]);
  const [rewardsWallet, setRewardsWallet] = useState({
    earned: 0,
    balance: 0,
    transactionCount: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const wallet = await getRewardsRetirementWallet();
      setRewardsWallet(wallet);

      const ledgerSnap = await getDocs(
        query(
          collection(db, "cashOutLedger"),
          orderBy("createdAt", "desc"),
          limit(200)
        )
      );
      setLedgerRows(ledgerSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to load Cash Out admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const metrics = useMemo(
    () =>
      aggregateCashOutAdminMetrics({
        ledgerRows,
        rewardsWallet,
      }),
    [ledgerRows, rewardsWallet]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ color: primaryBlue[800], fontWeight: 700 }}>
          Cash Out Program — Matrix &amp; System Wallets
        </Typography>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined" size="small">
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderLeft: `4px solid ${primaryBlue[600]}` }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Savings sx={{ color: primaryBlue[700] }} />
                <Typography variant="overline" color="text.secondary">
                  System Wallet — Rewards &amp; Retirement
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: primaryBlue[800] }}>
                {formatInr(rewardsWallet.balance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total earned: {formatInr(rewardsWallet.earned)}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {rewardsWallet.transactionCount} credits · ₹150 per COP enrollment + ₹150
                per Step 4 fill (₹1,200 unit)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Live activity — COP money-flow ledger
              </Typography>
              <Typography variant="h6">
                {metrics.ledgerEntryCount} entries
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sum of logged line amounts (matrix payouts, upgrades, Step 4
                splits): {formatInr(metrics.ledgerTotalVolume)}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Updates when members enroll in COP and matrix fills run. Not the
                same as Rewards wallet balance above.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Matrix rules (spreadsheet reference)
          </Typography>
          <Chip label="Not live money" size="small" color="default" variant="outlined" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This table is the <strong>written plan</strong> for one member who completes
          all 4 levels (3 fills each). The last column &quot;Cumulative&quot; ends at{" "}
          {formatInr(metrics.planGrandCashOut)} — that is <strong>not</strong> a
          platform balance or amount owed by the company today; it is only the math
          if one COP ID finished the full cycle. Levels 1–3: fill #1 → Cash Out Box;
          fills #2–#3 → upgrade pool. Level 4: each ₹1,200 fill → ₹750 cash wallet +
          ₹150 main AFP + ₹150 COP box + ₹150 Rewards &amp; Retirement.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell align="right">Auto Fill</TableCell>
                <TableCell align="right">Fill Count</TableCell>
                <TableCell align="right">TOTAL</TableCell>
                <TableCell align="right">Auto Upgrade</TableCell>
                <TableCell align="right">Cash out Money</TableCell>
                <TableCell align="right">Cumulative</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.planRows.map((r) => (
                <TableRow key={r.level}>
                  <TableCell>{r.level}</TableCell>
                  <TableCell align="right">{formatInr(r.autoFill)}</TableCell>
                  <TableCell align="right">{r.fillCount}</TableCell>
                  <TableCell align="right">{formatInr(r.total)}</TableCell>
                  <TableCell align="right">
                    {r.level === 4 ? r.autoUpgradeLabel : formatInr(r.autoUpgrade)}
                  </TableCell>
                  <TableCell align="right">{formatInr(r.cashOutMoney)}</TableCell>
                  <TableCell align="right">{formatInr(r.cumulativeCashOut)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Recent COP ledger
        </Typography>
        {ledgerRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No ledger entries yet. Enroll a Cash Out Program on a qualifying cart order to
            start the matrix.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>To wallet</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerRows.slice(0, 40).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Chip label={row.transactionType} size="small" />
                    </TableCell>
                    <TableCell>{row.programId || "—"}</TableCell>
                    <TableCell align="right">{formatInr(row.amount)}</TableCell>
                    <TableCell>{row.toWallet || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {row.reference || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default CashOutAdminFlowDashboard;
